import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Catches unhandled rendering errors anywhere below it so a single component
 * failure shows a recoverable message instead of blanking the whole app
 * (white screen of death). Error boundaries must be class components.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Unhandled error caught by ErrorBoundary:", error, info);
  }

  handleReload = () => {
    // Navigate home and do a full reload so the errored subtree is remounted.
    window.location.assign("/");
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="container"
          style={{ marginTop: "120px", maxWidth: 600 }}
        >
          <div className="alert alert-danger">
            <h4 className="alert-heading">Something went wrong</h4>
            <p>
              An unexpected error occurred. Try reloading the page. If the
              problem persists, please contact support.
            </p>
            {this.state.error?.message && (
              <pre className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
                {this.state.error.message}
              </pre>
            )}
            <button className="btn btn-primary" onClick={this.handleReload}>
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
